import { CruiseSearchContext } from 'contexts/CruiseSearchContext';
import PropTypes from 'prop-types';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import colors from 'styles/colors';
import { Button, FilterGrid } from './components';

import { CUSTOM, DEPARTURE_PORT, DESTINATION, NIGHTS, PORT, PROMO, SHIP, START_DATE } from 'contexts/CruiseSearchContext/constants';
import { getObjectFromQuery, getQueryFromObject } from 'contexts/CruiseSearchContext/utils/uniqueDestinationsUtils';
import { getBrandUISettings } from 'lib/utilities/Brands';
import { useFeatureFlags } from '../../../../hooks/LaunchDarkly/useFeatureFlags';
import { SectionTitle } from '../../components';

const Container = styled.section`
	background-color: ${colors.primary.ice};
	left: 0;
	padding: 0;
	width: 100%;
	z-index: 2;
`;

const CustomFilters = ({ title, keyName, updateCustomFilters = () => {}, handleClickCustom = () => {} }) => {
	const {
		searchSelecting,
		fetchAndUpdateFacetOptions,
		removeFromSearchSelectingFilter,
		removeAllFilters,
		addToSearchSelectingFilter,
		updateMonthsSelectingFilter,
		getUniqueDestinationsState,
		brand,
		getStatePromotions,
		updateAdditionalFilters,
		setPromotionSelected,
		promotionSelected,
		filterOptionsDictionary,
	} = useContext(CruiseSearchContext);
	const flags = useFeatureFlags();
	const { uiSettings } = getBrandUISettings(brand);
	const { custom = [] } = filterOptionsDictionary || {};
	const [isNotEmpty, setIsNotEmpty] = useState(false);

	const containerRef = useRef();

	const specialFilter = useMemo(() => {
		const specialFilters = getUniqueDestinationsState(keyName, promotionSelected);
		return specialFilters;
	}, [custom, searchSelecting, getUniqueDestinationsState, promotionSelected]);

	const validateKeyName = (keyName) => {
		return specialFilter.flatMap(option => option.containers).includes(keyName);
	};

	const updateSelectionCustomFilters = (filters, isSelected, paramValue, filterName) => {
		const startDateFilters = [];
		if (!Object.keys(filters || {})?.length && paramValue.includes(PROMO)) {
			const paramsToSend = isSelected ? null : paramValue;
			updateAdditionalFilters(paramsToSend);
		}

		Object.entries(filters).forEach(([key, value]) => {
			const filterValues = value?.split(',');
			filterValues?.forEach((newValue) => {
				if (isSelected) {
					removeFromSearchSelectingFilter(key, newValue, fetchAndUpdateFacetOptions, filterName);
				} else {
					if (key === START_DATE) {
						startDateFilters.push(newValue);
					}
					const filterToSend = filters || {};
					const uniqueDestination = {
						paramValue,
						filterName,
						filters: {
							values: Object.values(filterToSend),
							keys: Object.keys(filterToSend),
						},
					};
					addToSearchSelectingFilter(key, newValue, fetchAndUpdateFacetOptions, uniqueDestination);
				}
			});
		});

		return startDateFilters;
	};

	const handleClickFilters = (filterName, selected, isPromo, urlParams, criteria) => {
		if (criteria?.[PROMO]) {
			const query = criteria;

			const promoCode = query.promo?.[0];
			const promo = promotionSelected === promoCode ? null : promoCode;
			setPromotionSelected(promo);
			updateFilters(filterName, selected, isPromo, undefined, criteria);
			getStatePromotions({ displayQualifier: true, promo, toogles: flags });
		} else {
			updateFilters(filterName, selected, isPromo, urlParams, criteria);
		}
	};

	const addAditionalParams = ({ criteria = {}, customFilter, searchSelecting = {} }) => {
		let additionalParams = customFilter || '';
		const addParam = (base, newParam) => (base === '' ? newParam : `${base}|${newParam}`);

		const { adults: adultsApplied = [], children: childrenApplied = [] } = searchSelecting || {};
		const { adults = adultsApplied, children = childrenApplied } = criteria;

		const [paramAdults] = adults;
		const [paramChildren] = children;

		additionalParams = paramAdults ? addParam(additionalParams, `adults:${paramAdults}`) : additionalParams;
		additionalParams = paramChildren ? addParam(additionalParams, `children:${paramChildren}`) : additionalParams;
		return additionalParams;
	};

	const updateFilters = (filterName, selected, isPromo, urlParams, criteria) => {
		let filters, paramValue;

		if (criteria?.[PROMO]) {
			const query = criteria;
			const {
				[START_DATE]: startDate,
				[SHIP]: ship,
				[DESTINATION]: destination,
				[DEPARTURE_PORT]: departurePort,
				[PORT]: port,
				[NIGHTS]: nights,
				...rest
			} = query;
			const customFilter = getQueryFromObject({ startDate, ship, destination, departurePort, port, nights }, '|', ':');
			paramValue = getQueryFromObject(rest, '&', '=');
			const filterValue = addAditionalParams({ customFilter, criteria, searchSelecting });
			filters = getObjectFromQuery(filterValue, '|', ':') || {};
		} else {
			paramValue = urlParams
				.map((param) => {
					return `${param['key']}:${param['value']}`;
				})
				.join('&');
			const customFilter = `${CUSTOM}:${filterName}`;
			const filterValue = addAditionalParams({ customFilter, criteria, searchSelecting });
			filters = getObjectFromQuery(filterValue, '|', ':') || {};
		}

		if (selected) {
			if (!isPromo) {
				setPromotionSelected(null);
			}
			removeAllFilters();
		}
		const startDateFilters = updateSelectionCustomFilters(filters, !selected, paramValue, filterName);
		updateMonthsSelectingFilter(START_DATE, startDateFilters);

		handleClickCustom(selected);
	};

	useEffect(() => {
		setIsNotEmpty(validateKeyName(keyName));
	}, [keyName]); // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		updateCustomFilters(specialFilter);
	}, [specialFilter]); // eslint-disable-line react-hooks/exhaustive-deps

	if (isNotEmpty) {
		return (
			<Container ref={containerRef}>
				<SectionTitle title={title} />
				<FilterGrid gap={uiSettings.filtersModal.filtersGrid.gap}>
					{Array.isArray(specialFilter) &&
						specialFilter.map((option, i) => {
							if (option.containers.includes(keyName)) {
								return (
									<Button
										key={`${keyName}${option.code}-${i}`}
										id={`custom-filter-label-${option.code}`}
										showX={false}
										onClick={() => {
											handleClickFilters(option.code, !option.selected, option.isPromo, option.urlParams, option.criteria);
										}}
										selected={option.selected}
										isPromo={option.isPromo}
									>
										{option.label}
									</Button>
								);
							} else {
								return <></>;
							}
						})}
				</FilterGrid>
			</Container>
		);
	}
	return <></>;
};

CustomFilters.propTypes = {
	title: PropTypes.string,
	keyName: PropTypes.string,
	updateCustomFilters: PropTypes.func,
	handleClickCustom: PropTypes.func,
};

export default CustomFilters;
